/**
 * **Partner Management** — nur für Admins des Master-Mandanten (FIREIQ, `configuration.shouldSeePartnerManagement`).
 *
 * Liste der Partner-Mandanten und ein Wizard zum Anlegen: erster Admin, Unternehmensdaten, Bestätigung.
 * Dieselben Angaben wie bei /signup, ohne Produktwahl und Zahlung; der Partner bekommt automatisch die
 * Angebote, die der Master gerade hat (der letzte Schritt zeigt sie). Der Admin des Partners wird per
 * Mail eingeladen. Löschen gibt es nicht (tri-server docs/partner-management.md).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import { Handshake, Loader2, Plus } from "lucide-react";
import { authenticationAtom } from "../State";
import { useModalContext } from "../components/ModalProvider";
import AddressEntry from "../components/AddressEntry";
import PhoneInput from "../components/PhoneInput";
import { NewPartner, Partner, PartnerOffer, usePartnerAPI } from "../PartnerAPI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Stepper } from "@/components/ui/stepper";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");

const emptyAdmin = { email: "", firstName: "", lastName: "", mobileNumber: "" };
const emptyCompany = { name: "", tradeRegisterId: "", tradeRegisterCity: "", taxId: "" };

function Field({ id, label, value, onChange, required, type = "text" }: { id: string; label: string; value: string; onChange: (v: string) => void; required?: boolean; type?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true" className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      <Input id={id} type={type} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

export default function PartnerManagementPanel() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const { pushError } = useModalContext() as any;
  const api = usePartnerAPI();
  const customer = String(authentication?.customer ?? "");
  const [partners, setPartners] = useState<Partner[] | null>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [admin, setAdmin] = useState(emptyAdmin);
  const [company, setCompany] = useState(emptyCompany);
  const [address, setAddress] = useState<any>({ country: "DE" });
  const [offers, setOffers] = useState<PartnerOffer[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<string | null>(null);

  const load = async () => {
    try {
      setPartners((await api.partners(customer)).partners);
    } catch (error: any) {
      pushError(error);
      setPartners([]);
    }
  };
  useEffect(() => {
    if (customer) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const start = async () => {
    setAdmin(emptyAdmin);
    setCompany(emptyCompany);
    setAddress({ country: "DE" });
    setStep(0);
    setCreated(null);
    setOffers(null);
    setOpen(true);
    try {
      setOffers((await api.offers(customer)).offers);
    } catch (error: any) {
      pushError(error);
      setOffers([]);
    }
  };

  const adminValid = EMAIL.test(admin.email.trim()) && !!admin.firstName.trim() && !!admin.lastName.trim() && (!admin.mobileNumber || isPossiblePhoneNumber(admin.mobileNumber));
  const companyValid = !!company.name.trim() && !!(address.street && address.streetNumber && address.postalCode && address.city);

  const submit = async () => {
    setSaving(true);
    try {
      const body: NewPartner = {
        admin: { email: admin.email.trim(), firstName: admin.firstName.trim(), lastName: admin.lastName.trim(), ...(admin.mobileNumber ? { mobileNumber: admin.mobileNumber } : {}) },
        company: {
          name: company.name.trim(),
          tradeRegisterId: company.tradeRegisterId.trim() || undefined,
          tradeRegisterCity: company.tradeRegisterCity.trim() || undefined,
          taxId: company.taxId.trim() || undefined,
          street: address.street,
          streetNumber: address.streetNumber,
          postalCode: address.postalCode,
          city: address.city,
          country: address.country || "DE",
        },
      };
      const result = await api.create(customer, body);
      setCreated(result.invitationSent ? t("partnerManagement.createdInvited", { name: result.name, email: body.admin.email }) : t("partnerManagement.createdNoMail", { name: result.name }));
      setStep(3);
      await load();
    } catch (error: any) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { id: "admin", label: t("partnerManagement.stepAdmin") },
    { id: "company", label: t("partnerManagement.stepCompany") },
    { id: "confirm", label: t("partnerManagement.stepConfirm") },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4" data-testid="partner-management">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">{t("partnerManagement.intro")}</p>
        <Button onClick={start}>
          <Plus className="mr-1 h-4 w-4" /> {t("partnerManagement.add")}
        </Button>
      </div>

      {!partners ? (
        <Skeleton className="h-40 w-full" />
      ) : partners.length ? (
        <div className="min-w-0 overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("partnerManagement.colName")}</TableHead>
                <TableHead>{t("partnerManagement.colTenant")}</TableHead>
                <TableHead>{t("partnerManagement.colAdmin")}</TableHead>
                <TableHead>{t("partnerManagement.colAddress")}</TableHead>
                <TableHead className="text-right">{t("partnerManagement.colOffers")}</TableHead>
                <TableHead>{t("partnerManagement.colCreated")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="font-mono text-xs">{p.id}</TableCell>
                  <TableCell>
                    {p.admin ? (
                      <div className="flex flex-col">
                        <span>{p.admin.name || p.admin.email}</span>
                        <span className="text-xs text-muted-foreground">{p.admin.email}</span>
                        {p.admin.invitationPending ? (
                          <Badge variant="outline" className="mt-0.5 w-fit text-[10px]">
                            {t("partnerManagement.invitationPending")}
                          </Badge>
                        ) : null}
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {p.address ? `${[p.address.street, p.address.streetNumber].filter(Boolean).join(" ")}, ${[p.address.postalCode, p.address.city].filter(Boolean).join(" ")}` : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{p.activeOffers}</TableCell>
                  <TableCell className="tabular-nums">{day(p.created)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-md border p-8 text-center text-sm text-muted-foreground">
          <Handshake className="h-8 w-8" aria-hidden />
          {t("partnerManagement.empty")}
        </div>
      )}

      <Dialog open={open} onOpenChange={value => !saving && setOpen(value)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("partnerManagement.add")}</DialogTitle>
            <DialogDescription>{t("partnerManagement.wizardIntro")}</DialogDescription>
          </DialogHeader>
          <Stepper steps={steps} current={step} label={t("partnerManagement.add")} completeLabel={t("partnerManagement.done")} />

          {step === 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field id="partnerAdminEmail" label={t("partnerManagement.email")} type="email" value={admin.email} onChange={v => setAdmin({ ...admin, email: v })} required />
              </div>
              <Field id="partnerAdminFirstName" label={t("partnerManagement.firstName")} value={admin.firstName} onChange={v => setAdmin({ ...admin, firstName: v })} required />
              <Field id="partnerAdminLastName" label={t("partnerManagement.lastName")} value={admin.lastName} onChange={v => setAdmin({ ...admin, lastName: v })} required />
              <div className="min-w-0 overflow-x-auto sm:col-span-2">
                <PhoneInput id="partnerAdminMobile" label={t("partnerManagement.mobileNumber")} value={admin.mobileNumber} onChange={(value: string) => setAdmin({ ...admin, mobileNumber: value })} onValidationChange={() => {}} />
              </div>
              <p className="text-xs text-muted-foreground sm:col-span-2">{t("partnerManagement.inviteHint")}</p>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="flex flex-col gap-3">
              <Field id="partnerCompanyName" label={t("partnerManagement.companyName")} value={company.name} onChange={v => setCompany({ ...company, name: v })} required />
              <div className="grid gap-3 sm:grid-cols-3">
                <Field id="partnerTradeRegisterId" label={t("partnerManagement.tradeRegisterId")} value={company.tradeRegisterId} onChange={v => setCompany({ ...company, tradeRegisterId: v })} />
                <Field id="partnerTradeRegisterCity" label={t("partnerManagement.tradeRegisterCity")} value={company.tradeRegisterCity} onChange={v => setCompany({ ...company, tradeRegisterCity: v })} />
                <Field id="partnerTaxId" label={t("partnerManagement.taxId")} value={company.taxId} onChange={v => setCompany({ ...company, taxId: v })} />
              </div>
              <p className="pt-1 text-sm font-medium">{t("partnerManagement.companyAddress")}</p>
              <div className="min-w-0 overflow-x-auto">
                <AddressEntry address={address} addressChanged={(value: any) => setAddress(value)} />
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="flex flex-col gap-3 text-sm">
              <div className="rounded-md border p-3">
                <div className="font-medium">{company.name}</div>
                <div className="text-muted-foreground">
                  {[address.street, address.streetNumber].filter(Boolean).join(" ")}, {[address.postalCode, address.city].filter(Boolean).join(" ")}
                </div>
                <div className="mt-2">
                  {t("partnerManagement.firstAdmin")}: {admin.firstName} {admin.lastName} · {admin.email}
                </div>
              </div>
              <div>
                <div className="mb-1 font-medium">{t("partnerManagement.offersTitle")}</div>
                {!offers ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : offers.length ? (
                  <ul className="list-disc pl-5">
                    {offers.map(o => (
                      <li key={o.serviceReference}>
                        {o.name}
                        <span className="text-muted-foreground">
                          {" "}
                          · {o.planName ?? "—"} · {t("partnerManagement.validUntil", { date: day(o.validTo) })}
                          {o.source === "own" ? ` · ${t("partnerManagement.ownOffer")}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">{t("partnerManagement.noOffers")}</p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">{t("partnerManagement.offersHint")}</p>
              </div>
            </div>
          ) : null}

          {step === 3 && created ? <p className="text-sm">{created}</p> : null}

          <DialogFooter className="gap-2">
            {step > 0 && step < 3 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)} disabled={saving}>
                {t("global.back")}
              </Button>
            ) : null}
            {step === 0 ? (
              <Button onClick={() => setStep(1)} disabled={!adminValid}>
                {t("global.next")}
              </Button>
            ) : null}
            {step === 1 ? (
              <Button onClick={() => setStep(2)} disabled={!companyValid}>
                {t("global.next")}
              </Button>
            ) : null}
            {step === 2 ? (
              <Button onClick={submit} disabled={saving || !offers}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t("partnerManagement.create")}
              </Button>
            ) : null}
            {step === 3 ? <Button onClick={() => setOpen(false)}>{t("partnerManagement.close")}</Button> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
